import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { ErrorModal } from "../ModalAlert";
import supabase from "../../supabase";
import moment from "moment";
import TicketsTable from "../ticketsTable";
import { LoaderSpinner } from "../spinner/loader";
import { useNavigate } from "react-router-dom";
// import OrderDetailsForm from "../../AdminPages/orderDetailsForm";

const TicketsScreen = ({ 
  isAdmin = false, 
  clientId = null,
  selectedTickets = null,
  setSelectedTickets = null,
  setTotalExpenses = null,
  setTotalExpensesWithIva = null,
  setTotalIva210 = null,
  setTotalIva105 = null
}) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // const getFiles = async (bucketName) => {
  //   try {
  //     const { data, error } = await supabase.storage.from(bucketName).list();
  //     if (error) {
  //       throw new Error(error.message);
  //     }
  //     const files = await Promise.all(
  //       data.map(async (file) => {
  //         const { data: fileData, error: fileError } = await supabase.storage
  //           .from(bucketName)
  //           .getPublicUrl(file.name);
  //         if (fileError) {
  //           throw new Error(fileError.message);
  //         }
  //         return fileData.publicUrl;
  //       })
  //     );
  //     return files;
  //   } catch (error) {
  //     ErrorModal({ title: error.message });
  //     return [];
  //   }
  // };

  const getTickets = useCallback(async () => {
    setLoading(true);
    try {
      if (!clientId) {
        throw new Error("ID de cliente no válido");
      }

      const { data: ticketsData, error } = await supabase
        .from("tickets")
        .select(
          `
          *,
          ticketDetails(*)
        `
        )
        .eq("clientId", clientId)
        .eq("status", 2);

      if (error) {
        throw new Error(error.message);
      }

      // Asegurarnos que ticketsData no sea undefined
      if (!ticketsData) {
        setTickets([]);
        return;
      }

      const finalTickets = await Promise.all(
        ticketsData.map(async (ticket) => {
          const date = moment(ticket.createdAt).format("DD/MM/YY");

          // Obtenemos las órdenes relacionadas con este ticket (sin detalles para evitar duplicaciones)
          const { data: ordersData, error: ordersError } = await supabase
            .from("orders")
            .select("*")
            .eq("ticketId", ticket.ticketId);

          if (ordersError) {
            console.error("Error al obtener órdenes:", ordersError);
          }

          // Obtenemos los detalles de cada orden por separado
          const relatedOrders = await Promise.all(
            (ordersData || []).map(async (order) => {
              const { data: orderDetailsData, error: detailsError } = await supabase
                .from("orderDetails")
                .select("*")
                .eq("orderId", order.orderId);

              if (detailsError) {
                console.error(`Error obteniendo detalles para orden ${order.orderId}:`, detailsError);
                return { ...order, orderDetails: [] };
              }

              return {
                ...order,
                orderDetails: orderDetailsData || []
              };
            })
          );

          if (ordersError) {
            console.error("Error al obtener órdenes:", ordersError);
          }

          // Asegurarnos que relatedOrders no sea undefined
          const safeRelatedOrders = relatedOrders || [];

          // Construimos el comprobante base (sin los links, ya que se manejarán en la tabla)
          const comprobante = ticket.ticketLetter
            ? `${ticket.ticketType} ${ticket.ticketNumber}`
            : "No disponible";

          const orderCurrencyRate = (() => {
            const first = safeRelatedOrders[0];
            if (!first) return 1;
            const r = parseFloat(first.currencyRate);
            return !isNaN(r) && r > 0 ? r : 1;
          })();

          const lineMultiplier = (row) => {
            const isArs = row.currency === 1 || row.currency === "1";
            return isArs ? 1 : orderCurrencyRate;
          };

          const totalWithIvaPesos = (ticket.ticketDetails || []).reduce(
            (sum, row) => {
              const price = Number(row.price) || 0;
              const ivaPercentage = Number(row.ivaPercentage) || 0;
              const quantity = Number(row.quantity) || 0;
              const mult = lineMultiplier(row);
              return (
                sum +
                price *
                  (1 + ivaPercentage / 100) *
                  quantity *
                  mult
              );
            },
            0
          );

          return {
            ...ticket,
            date,
            comprobante,
            totalAmount: Number(totalWithIvaPesos.toFixed(2)),
            relatedOrders: safeRelatedOrders,
          };
        })
      );
      setTickets(finalTickets);
    } catch (error) {
      ErrorModal({ title: error.message });
      setTickets([]); // Establecemos un array vacío en caso de error
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (clientId) {
      getTickets();
    }
  }, [clientId, getTickets]);

  return (
    <Box
      sx={{
        height: "auto",
        width: "100%",
        margin: "0 20px",
        marginBottom: "0px",
        overflow: "visible",
      }}
    >
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoaderSpinner />
        </div>
      ) : (
        <TicketsTable 
          tickets={tickets} 
          isAdmin={isAdmin} 
          clientId={clientId}
          selectedTickets={selectedTickets}
          setSelectedTickets={setSelectedTickets}
          setTotalExpenses={setTotalExpenses}
          setTotalExpensesWithIva={setTotalExpensesWithIva}
          setTotalIva210={setTotalIva210}
          setTotalIva105={setTotalIva105}
        />
      )}
    </Box>
  );
};

export default TicketsScreen;
